import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import StatisticsPage, { drilldownPath } from '../../components/statistics/StatisticsPage';
import DataTable, { Column } from '../../components/statistics/DataTable';
import { BarList, Heatmap } from '../../components/statistics/charts';
import { useStatisticsData, useStatisticsFilters } from '../../components/statistics/useStatistics';
import { getMarketplace } from '../../services/statisticsService';
import { WEEKDAYS, WINDOWS, formatCount, formatDay, formatDecimal, formatShare } from '../../utils/period';

interface RouteRow {
  origin: string;
  destination: string;
  rides: number;
  seatsOffered: number;
  requests: number;
  seatsFilled: number;
  fillRate: number | null;
  unmetCaptures: number;
  unmetRiders: number;
  averagePrice: number | null;
}

const CELL_MEASURES = [
  { key: 'rides', label: 'Rides' },
  { key: 'seatsOffered', label: 'Seats offered' },
  { key: 'requests', label: 'Seat requests' },
  { key: 'seatsFilled', label: 'Seats filled' },
];

/** PRD §5.2 — where to recruit drivers, and for which part of the week. */
const Marketplace = () => {
  const [filters, setFilters] = useStatisticsFilters();
  const [search, setSearch] = useSearchParams();
  const origin = search.get('origin');
  const destination = search.get('destination');
  const route = origin && destination ? { origin, destination } : null;
  const scope: 'period' | 'upcoming' = search.get('scope') === 'upcoming' ? 'upcoming' : 'period';
  const [measure, setMeasure] = useState('rides');

  const { data, loading, error, reload } = useStatisticsData(
    (f) => getMarketplace(f, route, scope),
    filters,
    `${origin}|${destination}|${scope}`
  );

  const setScope = (next: 'period' | 'upcoming') => {
    const params = new URLSearchParams(search);
    if (next === 'upcoming') params.set('scope', 'upcoming');
    else params.delete('scope');
    setSearch(params, { replace: true });
  };

  /** Which rides the figures cover, in words: "departing 6 Oct – 4 Nov" or "departing from now on". */
  const ridesWindow: { from: string; to: string | null; since: string | null } | undefined = data?.rides;
  const ridesText =
    scope === 'upcoming'
      ? `departing from now on${ridesWindow?.since ? ` (${formatDay(ridesWindow.since.slice(0, 10))}, ${ridesWindow.since.slice(11, 16)})` : ''}`
      : ridesWindow?.to
        ? `departing ${formatDay(ridesWindow.from)} – ${formatDay(ridesWindow.to)}`
        : 'departing in the period';

  const selectRoute = (next: { origin: string; destination: string } | null) => {
    const params = new URLSearchParams(search);
    if (next) {
      params.set('origin', next.origin);
      params.set('destination', next.destination);
    } else {
      params.delete('origin');
      params.delete('destination');
    }
    setSearch(params, { replace: true });
  };

  const routes: RouteRow[] = data?.routes || [];
  const needy = routes.filter((r) => r.unmetRiders > 0 || r.requests > r.seatsFilled).slice(0, 3);
  const cells: any[] = data?.byTime?.cells || [];
  const cell = (weekday: string, window: string) => cells.find((c) => String(c.weekday) === weekday && c.window === window);
  const isSelected = (r: RouteRow) => route?.origin === r.origin && route?.destination === r.destination;

  const columns: Column<RouteRow>[] = [
    {
      key: 'route',
      label: 'Route',
      value: (r) => `${r.origin} → ${r.destination}`,
      render: (r) => (
        <button
          type="button"
          className="stats-link-button"
          onClick={() => selectRoute(isSelected(r) ? null : r)}
          title="Show this route by day and time"
        >
          {r.origin} → {r.destination}
        </button>
      ),
    },
    { key: 'unmetRiders', label: 'Riders waiting now', numeric: true },
    { key: 'rides', label: 'Rides', numeric: true },
    { key: 'seatsOffered', label: 'Seats offered', numeric: true },
    { key: 'requests', label: 'Seat requests', numeric: true },
    { key: 'seatsFilled', label: scope === 'upcoming' ? 'Seats filled so far' : 'Seats filled', numeric: true },
    { key: 'fillRate', label: scope === 'upcoming' ? 'Filled so far' : 'Fill rate', numeric: true, render: (r) => formatShare(r.fillRate) },
    { key: 'averagePrice', label: 'Avg price (₪)', numeric: true, render: (r) => formatDecimal(r.averagePrice) },
  ];

  const unmetByWindow = data?.byTime?.unmetByWindow || {};
  const unmetByWeekday = data?.byTime?.unmetByWeekday || {};

  return (
    <StatisticsPage
      title="Marketplace"
      subtitle="Supply and demand by route. Supply is the rides counted below; demand is riders waiting right now — members watching a route with a Ride Capture that has never matched."
      filters={filters}
      onFiltersChange={setFilters}
      period={data?.period}
      loading={loading}
      error={error}
      onRetry={reload}
      showComparison={false}
    >
      <div className="stats-scope" role="tablist" aria-label="Which rides to count">
        <button
          type="button"
          role="tab"
          aria-selected={scope === 'period'}
          className={scope === 'period' ? 'stats-scope-option active' : 'stats-scope-option'}
          onClick={() => setScope('period')}
        >
          Rides that departed in the period
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={scope === 'upcoming'}
          className={scope === 'upcoming' ? 'stats-scope-option active' : 'stats-scope-option'}
          onClick={() => setScope('upcoming')}
        >
          Upcoming rides (from now on)
        </button>
      </div>
      <p className="stats-note stats-scope-note">
        {scope === 'upcoming'
          ? 'Counting every ride still to depart, whatever the period above. Riders waiting are always as of now, so the period has no effect in this view.'
          : 'Counting rides whose departure date falls in the period above. Rides still to depart are under "Upcoming rides". Riders waiting are always as of now.'}
      </p>

      {needy.length > 0 && (
        <div className="stats-callout">
          <strong>Most in need of drivers:</strong>{' '}
          {needy.map((r, i) => (
            <span key={`${r.origin}-${r.destination}`}>
              {i > 0 && ' · '}
              {r.origin} → {r.destination} ({formatCount(r.unmetRiders)} waiting, {formatCount(r.requests - r.seatsFilled)} requests unfilled)
            </span>
          ))}
        </div>
      )}

      <section className="stats-section">
        <h2>Routes — rides {ridesText}</h2>
        <p className="stats-section-intro">
          Cancelled rides are left out. "Riders waiting now" is as of this moment, whatever the dates. Click a route to see
          it by day and time below.{' '}
          {scope === 'upcoming' ? (
            <Link className="stats-link" to="/rides">
              Follow each upcoming ride on the Rides page
            </Link>
          ) : (
            <Link className="stats-link" to={drilldownPath('rides', filters)}>
              See every ride posted in the period
            </Link>
          )}
        </p>
        <DataTable
          columns={columns}
          rows={routes}
          exportName={`zippel-routes-${data?.period?.from}-to-${data?.period?.to}`}
          rowKey={(r) => `${r.origin}|${r.destination}`}
          empty={scope === 'upcoming' ? 'No upcoming rides and nobody waiting.' : 'No rides or waiting riders in this period.'}
        />
      </section>

      <section className="stats-section">
        <h2>
          By day of the week and time{route ? `: ${route.origin} → ${route.destination}` : ', all routes'} — rides {ridesText}
        </h2>
        <p className="stats-section-intro">
          Each cell adds up every ride on that weekday and time of day across those dates: "Sun · Morning" is all Sunday
          mornings together, not one particular day. A route can look healthy in a daily total and still have nothing in
          the evening.
          {route && (
            <>
              {' '}
              <button type="button" className="stats-link-button" onClick={() => selectRoute(null)}>
                Show all routes
              </button>
            </>
          )}
        </p>
        <label className="stats-control" style={{ marginBottom: 12, maxWidth: 220 }}>
          <span>Show</span>
          <select value={measure} onChange={(e) => setMeasure(e.target.value)}>
            {CELL_MEASURES.map((m) => (
              <option key={m.key} value={m.key}>
                {m.label}
              </option>
            ))}
          </select>
        </label>
        <Heatmap
          rows={WEEKDAYS.map((label, i) => ({ key: String(i), label }))}
          columns={WINDOWS}
          value={(row, column) => cell(row, column)?.[measure] || 0}
          describe={(row, column) => {
            const c = cell(row, column);
            return c
              ? `${WEEKDAYS[Number(row)]} ${column}: ${c.rides} rides, ${c.seatsOffered} seats, ${c.requests} requests, ${c.seatsFilled} filled`
              : '';
          }}
        />
      </section>

      <div className="stats-columns">
        <section className="stats-section">
          <h2>Riders waiting now, by time of day</h2>
          <p className="stats-section-intro">A Capture watching several windows counts once in each.</p>
          <BarList
            items={[...WINDOWS, { key: 'any', label: 'Any time' }].map((w) => ({
              label: w.label,
              value: unmetByWindow[w.key] || 0,
            }))}
          />
        </section>
        <section className="stats-section">
          <h2>Riders waiting now, by day of the week</h2>
          <p className="stats-section-intro">A Capture watching several days counts once on each.</p>
          <BarList
            items={[...WEEKDAYS.map((label, i) => ({ key: String(i), label })), { key: 'any', label: 'Any day' }].map((d) => ({
              label: d.label,
              value: unmetByWeekday[d.key] || 0,
            }))}
          />
        </section>
      </div>
    </StatisticsPage>
  );
};

export default Marketplace;
